import React from 'react';
import { SEOProtection } from '../../components/SEOProtection/SEOProtection.js';
import { Button } from '../../components/Button/Button.js';
import { Icon } from '../../components/Icon/Icon';
import { ThemeSelector } from '../../components/ThemeSelector/ThemeSelector.js';
import { CardColor } from '../../components/CardColor/CardColor.js';
import { FolderHeader } from '../../components/FolderHeader/FolderHeader.js';
import { parseColor, formatOklch, getRelativeChroma } from '../../utils/colorUtils.js';
import { getRandomDemoColor } from '../../utils/demoColors';
import { calculateLinkedColor, calculateContrastColor } from '../../utils/linkedColorUtils.js';
import { updateFavicon } from '../../utils/faviconUtils';
// @ts-ignore - CRA provides ReactComponent for SVG
import { ReactComponent as LogoDark } from '../../Logo-dark-mode.svg';
// @ts-ignore - CRA provides ReactComponent for SVG
import { ReactComponent as LogoLight } from '../../Logo-light-mode.svg';
// @ts-ignore - CRA provides ReactComponent for SVG
import { ReactComponent as IconDark } from '../../Icon-dark-mode.svg';
// @ts-ignore - CRA provides ReactComponent for SVG
import { ReactComponent as IconLight } from '../../Icon-light-mode.svg';
import '../../components/CardColor/CardColor.css';
import '../../components/FolderHeader/FolderHeader.css';
import '../../components/ThemeSelector/ThemeSelector.css';
import './Home.css';
import demoPageConfig from '../../presets/betterLCH - Demo page.json';
import { GrainGradient } from '@paper-design/shaders-react';

export default function Waitlist() {
  const [isInteractive, setIsInteractive] = React.useState(true);
  const [themePreference, setThemePreference] = React.useState<'system' | 'light' | 'dark'>(() => {
    // Load from localStorage or default to 'system'
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme-preference');
      if (saved === 'system' || saved === 'light' || saved === 'dark') {
        return saved;
      }
    }
    return 'system';
  });
  const [isThemeSwitching, setIsThemeSwitching] = React.useState(false);
  const [showFixedHeader, setShowFixedHeader] = React.useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);
  const [isPageVisible, setIsPageVisible] = React.useState(() =>
    typeof document !== 'undefined' ? !document.hidden : true
  );

  // Set tinted mode for Waitlist page on document root
  // and clear the global html/body background so the shader is visible.
  React.useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('tinted');

    const previousHtmlBg = root.style.backgroundColor;
    const previousBodyBg = body.style.backgroundColor;

    root.style.backgroundColor = 'transparent';
    body.style.backgroundColor = 'transparent';

    return () => {
      root.classList.remove('tinted');
      root.style.backgroundColor = previousHtmlBg;
      body.style.backgroundColor = previousBodyBg;
    };
  }, []);

  // Respect “Reduce motion” so the background shader doesn’t animate (saves CPU/GPU and accessibility).
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setPrefersReducedMotion(mq.matches);
    handleChange();
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  // Pause shader when the tab is hidden to avoid heating and fan noise.
  React.useEffect(() => {
    const handleVisibility = () => setIsPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Load config data
  const configData = React.useMemo(() => {
    try {
      const collection = demoPageConfig.data.collections[0];
      const mode = collection.modes[0];
      return {
        colors: mode.colors || [],
        folders: mode.folders || []
      };
    } catch (error) {
      console.error('Failed to load demo page config:', error);
      return { colors: [], folders: [] };
    }
  }, []);

  // Initialize once per mount with a random demo color
  const initialDemoRef = React.useRef(getRandomDemoColor());
  const initialPrimaryValueRef = React.useRef(initialDemoRef.current.oklchValue);
  const initialPrimaryNameRef = React.useRef(initialDemoRef.current.name);
  const [backgroundColors, setBackgroundColors] = React.useState({
    primary: initialDemoRef.current.hexValue,
    secondary: '#FFCD00'
  });
  const [primaryColorValue, setPrimaryColorValue] = React.useState(initialDemoRef.current.oklchValue);
  const [primaryColorName, setPrimaryColorName] = React.useState(initialDemoRef.current.name);
  const [staticColorOverrides, setStaticColorOverrides] = React.useState<{ [key: number]: string }>({});
  // Live overrides for linked colors on the page (reference + L/C/H/A). Keys are color ids
  const [linkedOverrides, setLinkedOverrides] = React.useState<Record<string, Partial<{ referenceColorId: string; lExpression: string; cExpression: string; hExpression: string; aExpression: string }>>>({});
  // Live overrides for contrast colors on the page (background + foreground + Lc + useAlpha). Keys are color ids
  const [contrastOverrides, setContrastOverrides] = React.useState<Record<string, Partial<{ backgroundColorId: string; foregroundColorId: string; contrastLevel: number; useAlpha: boolean }>>>({});
  
  // Base colors state (used in hero section)
  const [baseColorOverrides, setBaseColorOverrides] = React.useState<Record<string, any>>({});
  
  const footerRef = React.useRef<HTMLElement | null>(null);
  
  // Ref for banner to measure its height
  const bannerRef = React.useRef<HTMLDivElement>(null);

  // Derive banner colors: background = Secondary; text = contrast on Secondary (Lc 60)
  const bannerColors = React.useMemo(() => {
    try {
      const secondaryHex = backgroundColors.secondary || '#FFCD00';
      const allColors = [
        { id: 'bg', mode: 'static', colorValue: secondaryHex },
        { id: 'fg', mode: 'static', colorValue: secondaryHex }
      ] as any[];
      const result = calculateContrastColor(allColors as any, 'bg', 'fg', 60, false);
      const textHex = (result as any)?.hex || '#000000';
      return { bg: secondaryHex, fg: textHex };
    } catch {
      return { bg: backgroundColors.secondary || '#FFCD00', fg: '#000000' };
    }
  }, [backgroundColors.secondary]);

  React.useEffect(() => {
    const onResize = () => setIsInteractive(window.innerWidth >= 1280);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Measure banner height and update CSS variable
  React.useEffect(() => {
    const updateBannerHeight = () => {
      if (bannerRef.current && window.innerWidth <= 899) {
        const height = bannerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--small-screen-banner-height', `${height}px`);
      } else {
        document.documentElement.style.setProperty('--small-screen-banner-height', '0px');
      }
    };

    // Initial update
    updateBannerHeight();
    
    // Update on window resize
    window.addEventListener('resize', updateBannerHeight);
    
    // Use ResizeObserver to watch for banner content changes (text wrapping, etc.)
    const resizeObserver = new ResizeObserver(updateBannerHeight);
    if (bannerRef.current) {
      resizeObserver.observe(bannerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateBannerHeight);
      resizeObserver.disconnect();
    };
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      const heroButton = document.querySelector('.hero-button');
      if (!heroButton) return;
      
      const rect = heroButton.getBoundingClientRect();
      const isScrolledOut = rect.bottom < 0;
      setShowFixedHeader(isScrolledOut);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add a class on mount to trigger initial load animations
  React.useEffect(() => {
    const root = document.documentElement;
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      // Use double RAF to ensure initial styles apply before animating
      raf2 = requestAnimationFrame(() => {
        root.classList.add('demo-loaded');
      });
    });
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      root.classList.remove('demo-loaded');
    };
  }, []);

  // Get the effective theme based on preference
  const getEffectiveTheme = React.useCallback(() => {
    if (themePreference === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return themePreference;
  }, [themePreference]);

  // Apply theme to document (synchronously so DOM and React state stay in sync)
  const applyTheme = React.useCallback((theme: 'light' | 'dark') => {
    const root = document.documentElement;
    const currentTheme = root.getAttribute('data-theme');

    // Skip if already the correct theme
    if (currentTheme === theme) return;

    setIsThemeSwitching(true);
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      root.classList.add('theme-animating');
      root.setAttribute('data-theme', theme);
      setTimeout(() => {
        root.classList.remove('theme-animating');
        setIsThemeSwitching(false);
      }, 180);
    } else {
      root.setAttribute('data-theme', theme);
      setIsThemeSwitching(false);
    }
  }, []);

  // Handle theme preference change (apply DOM theme first so re-render sees consistent state)
  const handleThemePreferenceChange = React.useCallback((preference: 'system' | 'light' | 'dark') => {
    const effectiveTheme = preference === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : preference;
    applyTheme(effectiveTheme);
    setThemePreference(preference);
    localStorage.setItem('theme-preference', preference);
  }, [applyTheme]);

  // Apply theme on mount and when preference changes
  React.useEffect(() => {
    const effectiveTheme = getEffectiveTheme();
    applyTheme(effectiveTheme);
  }, [themePreference, getEffectiveTheme, applyTheme]);

  // Listen to system preference changes when in system mode
  React.useEffect(() => {
    if (themePreference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const effectiveTheme = mediaQuery.matches ? 'dark' : 'light';
      applyTheme(effectiveTheme);
    };

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference, applyTheme]);

  const scrollToTop = React.useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleBaseColorChange = (colorId: string, field: string, value: any) => {
    // Update primary color value when base-primary color changes
    if (colorId === 'base-primary' && field === 'colorValue') {
      setPrimaryColorValue(value);
    }
    
    setBaseColorOverrides(prev => ({
      ...prev,
      [colorId]: {
        ...(prev[colorId] || {}),
        [field]: value
      }
    }));
  };

  // Calculate static colors based on INITIAL Primary color only (do not react to edits)
  const staticColors = React.useMemo(() => {
    const valueToUse = initialPrimaryValueRef.current;
    const primaryParsed = parseColor(valueToUse);
    const primaryOklch = (primaryParsed as any)?.oklch;
    
    if (!primaryOklch) {
      return [];
    }

    const allColors = [{
      id: 'primary',
      mode: 'static' as const,
      colorValue: valueToUse,
      hexValue: (primaryParsed as any)?.hex || '#FF8C00',
    }];

    // Helper to calculate linked color
    const calculateColor = (lExpression: string, hExpression: string) => {
      return calculateLinkedColor(allColors, 'primary', lExpression, '', hExpression, '');
    };

    // Color #1: OKLCH format (L: *0.5+0.5, H: +30)
    const color1Result = calculateColor('*0.5+0.5', '+30');
    const color1Oklch = (color1Result as any)?.oklch;
    let color1Value = 'oklch(0.5 0.2 88)';
    if (color1Oklch) {
      const relativeChroma = getRelativeChroma(color1Oklch);
      const chromaPercent = Math.round(relativeChroma * 100);
      const l = Math.round(color1Oklch.l * 100) / 100;
      const h = Math.round(color1Oklch.h);
      color1Value = `oklch(${l} ${chromaPercent}% ${h})`;
    }
    const color1Display = staticColorOverrides[0] || color1Value;
    const color1DisplayParsed = parseColor(color1Display);

    // Color #2: HEX format (L: *0.65+0.35, H: +22)
    const color2Result = calculateColor('*0.65+0.35', '+22');
    const color2Value = (color2Result as any)?.hex || '#FF9A5C';
    const color2Display = staticColorOverrides[1] || color2Value;
    const color2DisplayParsed = parseColor(color2Display);

    // Color #3: HSL format (L: *0.8+0.2, H: +14)
    const color3Result = calculateColor('*0.8+0.2', '+14');
    const color3Hsl = (color3Result as any)?.hsl;
    let color3Value = 'hsl(30, 100%, 65%)';
    if (color3Hsl) {
      const h = Math.round(color3Hsl.h || 0);
      // Clamp S and L to [0, 100]
      const s = Math.max(0, Math.min(100, Math.round(((color3Hsl.s || 0) * 100))));
      const l = Math.max(0, Math.min(100, Math.round(((color3Hsl.l || 0) * 100))));
      color3Value = `hsl(${h}, ${s}%, ${l}%)`;
    }
    const color3Display = staticColorOverrides[2] || color3Value;
    const color3DisplayParsed = parseColor(color3Display);

    // Color #4: RGB format (L: *0.9+0.1, H: +7)
    const color4Result = calculateColor('*0.9+0.1', '+7');
    const color4Rgb = (color4Result as any)?.rgb;
    let color4Value = 'rgb(255, 180, 120)';
    if (color4Rgb) {
      // Clamp channels to [0, 255]
      const r = Math.max(0, Math.min(255, Math.round(((color4Rgb.r || 0) * 255))));
      const g = Math.max(0, Math.min(255, Math.round(((color4Rgb.g || 0) * 255))));
      const b = Math.max(0, Math.min(255, Math.round(((color4Rgb.b || 0) * 255))));
      color4Value = `rgb(${r}, ${g}, ${b})`;
    }
    const color4Display = staticColorOverrides[3] || color4Value;
    const color4DisplayParsed = parseColor(color4Display);

    // Color #5: Color name (INITIAL primary name or override)
    const color5Value = initialPrimaryNameRef.current;
    const color5Display = staticColorOverrides[4] || color5Value;
    const color5DisplayParsed = parseColor(color5Display);

    return [
      { name: 'OKLCH', value: color1Display, result: color1DisplayParsed, index: 0 },
      { name: 'HEX code', value: color2Display, result: color2DisplayParsed, index: 1 },
      { name: 'HSL', value: color3Display, result: color3DisplayParsed, index: 2 },
      { name: 'RGB', value: color4Display, result: color4DisplayParsed, index: 3 },
      { name: 'Color name', value: color5Display, result: color5DisplayParsed, index: 4 },
    ];
  }, [staticColorOverrides]);

  // Helper to get folder path from folderId
  const getFolderPath = React.useCallback((folderId: string | null | undefined): string => {
    if (!folderId) return '';
    const folder = configData.folders.find(f => f.id === folderId);
    if (!folder) return '';
    
    // Build path by traversing parent folders
    const pathParts: string[] = [];
    let currentFolder: typeof folder | undefined = folder;
    while (currentFolder) {
      pathParts.unshift(currentFolder.name);
      if (currentFolder.parentId) {
        currentFolder = configData.folders.find(f => f.id === currentFolder!.parentId);
      } else {
        currentFolder = undefined;
      }
    }
    return pathParts.join('/');
  }, [configData.folders]);

  // Transform config color to component format
  const transformConfigColor = React.useCallback((configColor: any): any => {
    const folderPath = getFolderPath(configColor.folderId);
    const folderName = folderPath.split('/').pop() || '';
    
    const baseColor: any = {
      id: configColor.id,
      mode: configColor.mode,
      name: configColor.name,
      colorName: configColor.name,
      folderPath,
      folderId: configColor.folderId || '',
      folderName,
      section: 'waitlist',
      colorValue: configColor.colorValue || configColor.hexValue || '#808080',
      hexValue: configColor.hexValue || '#808080',
    };

    if (configColor.mode === 'linked') {
      baseColor.referenceColorId = configColor.referenceColorId || '';
      baseColor.lExpression = configColor.lExpression || '';
      baseColor.cExpression = configColor.cExpression || '';
      baseColor.hExpression = configColor.hExpression || '';
      baseColor.aExpression = configColor.aExpression || '';
    } else if (configColor.mode === 'contrast') {
      baseColor.backgroundColorId = configColor.backgroundColorId || '';
      baseColor.foregroundColorId = configColor.foregroundColorId || '';
      baseColor.contrastLevel = configColor.contrastLevel || 60;
      baseColor.useAlpha = configColor.useAlpha || false;
    }

    return baseColor;
  }, [getFolderPath]);

  // Calculate linked color sections based on current Primary color (recalculates when Primary changes)
  const linkedColorSections = React.useMemo(() => {
    const primaryParsed = parseColor(primaryColorValue);
    const primaryOklch = (primaryParsed as any)?.oklch;
    
    if (!primaryOklch) {
      return { 
        lightness: [], 
        chroma: [], 
        hue: [], 
        alpha: [],
        allColors: [],
        precomputedColors: new Map()
      };
    }

    // Transform config colors to component format
    const configColors = configData.colors.map(transformConfigColor);
    
    // Find Primary color from config and override with random one
    const primaryConfigColor = configColors.find(c => c.name === 'Primary' && c.mode === 'static');
    const secondaryConfigColor = configColors.find(c => c.name === 'Secondary');
    
    // Create a mapping from config color IDs to component IDs
    const colorIdMap = new Map<string, string>();
    if (primaryConfigColor) {
      colorIdMap.set(primaryConfigColor.id, 'base-primary');
    }
    if (secondaryConfigColor) {
      colorIdMap.set(secondaryConfigColor.id, 'base-secondary');
    }
    
    // Helper to map color IDs recursively
    const mapColorId = (id: string): string => {
      if (colorIdMap.has(id)) {
        return colorIdMap.get(id)!;
      }
      // Check if this ID references a color that should be mapped
      const color = configColors.find(c => c.id === id);
      if (color && color.mode === 'linked' && color.referenceColorId) {
        const mappedRefId = mapColorId(color.referenceColorId);
        if (mappedRefId !== color.referenceColorId) {
          // This color references a mapped color, but we keep its own ID
          // The reference will be updated below
        }
      }
      return id;
    };
    
    const baseColorsDefault = configColors
      .filter(c => {
        const folderPath = c.folderPath || '';
        return folderPath === 'Base colors' || folderPath.split('/').pop() === 'Base colors';
      })
      .map(c => {
        let mappedColor = { ...c };
        
        // Override Primary color with random one
        if (c.name === 'Primary' && c.mode === 'static') {
          mappedColor = {
            ...mappedColor,
            id: 'base-primary',
            colorValue: primaryColorValue,
            hexValue: (primaryParsed as any)?.hex || '#FF8C00',
            folderPath: 'Base colors',
            folderName: 'Base colors',
          };
        }
        // Map Secondary to base-secondary
        else if (c.name === 'Secondary') {
          mappedColor = { ...mappedColor, id: 'base-secondary' };
        }
        // Map "Secondary on Primary" contrast color to base-secondary-on-primary
        else if (c.name === 'Secondary on Primary' && c.mode === 'contrast') {
          mappedColor = { ...mappedColor, id: 'base-secondary-on-primary' };
        }
        
        // Map reference colors to use mapped IDs (for all linked colors including Secondary)
        if (mappedColor.mode === 'linked' && mappedColor.referenceColorId) {
          mappedColor = { ...mappedColor, referenceColorId: mapColorId(mappedColor.referenceColorId) };
        }
        // Map contrast colors to use mapped IDs
        if (mappedColor.mode === 'contrast') {
          mappedColor = {
            ...mappedColor,
            backgroundColorId: mapColorId(mappedColor.backgroundColorId),
            foregroundColorId: mapColorId(mappedColor.foregroundColorId),
          };
        }
        
        return mappedColor;
      });

    // If no base colors found in config, fall back to default structure
    if (baseColorsDefault.length === 0) {
      baseColorsDefault.push({
        id: 'base-primary',
        mode: 'static' as const,
        colorValue: primaryColorValue,
        hexValue: (primaryParsed as any)?.hex || '#FF8C00',
        folderPath: 'Base colors',
        name: 'Primary',
        colorName: 'Primary',
        folderId: 'base-folder',
        folderName: 'Base colors',
        section: 'waitlist'
      });
    }

    // Apply base color overrides from user interactions
    const baseColors = baseColorsDefault.map(color => {
      const override = baseColorOverrides[color.id];
      if (!override) return color;
      return { ...color, ...override };
    });

    // Helper to create a linked color definition
    const createLinkedColor = (
      id: string,
      name: string,
      folderPath: string,
      referenceId: string,
      l: string = '',
      c: string = '',
      h: string = '',
      a: string = ''
    ) => ({
      id,
      mode: 'linked' as const,
      name: name,
      colorName: name,
      folderPath,
      folderId: folderPath.toLowerCase().replace(/\//g, '-'),
      folderName: folderPath.split('/').pop(),
      referenceColorId: referenceId,
      lExpression: l,
      cExpression: c,
      hExpression: h,
      aExpression: a,
      // Add section property to control dropdown organization
      section: 'waitlist'
    });

    // Apply user overrides if present
    const applyOverrides = (color: any) => {
      const ovr = linkedOverrides[color.id];
      if (!ovr) return color;
      return {
        ...color,
        referenceColorId: ovr.referenceColorId ?? color.referenceColorId,
        lExpression: ovr.lExpression ?? color.lExpression,
        cExpression: ovr.cExpression ?? color.cExpression,
        hExpression: ovr.hExpression ?? color.hExpression,
        aExpression: ovr.aExpression ?? color.aExpression,
      };
    };

    // Static colors (the 5 demo colors) - KEEP CURRENT LOGIC
    const staticColorObjects = staticColors.map((color, index) => ({
      id: `static-${index}`,
      mode: 'static' as const,
      colorName: color.name,
      name: color.name,
      colorValue: color.value,
      hexValue: (color.result as any)?.hex || '#808080',
      folderPath: 'Static colors',
      folderId: 'static-folder',
      folderName: 'Static colors',
      section: 'waitlist'
    }));

    // Group config colors by folder
    const lightnessFolder = configData.folders.find(f => f.name === 'Lightness');
    const chromaFolder = configData.folders.find(f => f.name === 'Chroma');
    const hueFolder = configData.folders.find(f => f.name === 'Hue');
    const alphaFolder = configData.folders.find(f => f.name === 'Alpha');
    const contrastSolidFolder = configData.folders.find(f => f.name === 'Solid');
    const contrastTransparentFolder = configData.folders.find(f => f.name === 'Transparent');

    // Helper to get colors in a folder
    const getColorsInFolder = (folderId: string | undefined) => {
      if (!folderId) return [];
      return configColors
        .filter(c => c.folderId === folderId)
        .map(c => {
          // Map reference colors to use mapped IDs
          if (c.mode === 'linked' && c.referenceColorId) {
            return { ...c, referenceColorId: mapColorId(c.referenceColorId) };
          }
          // Map contrast colors to use mapped IDs
          if (c.mode === 'contrast') {
            return {
              ...c,
              backgroundColorId: mapColorId(c.backgroundColorId),
              foregroundColorId: mapColorId(c.foregroundColorId),
            };
          }
          return c;
        })
        .map(applyOverrides);
    };

    // Lightness section colors from config
    const lightnessColors = lightnessFolder 
      ? getColorsInFolder(lightnessFolder.id)
      : [].map(applyOverrides);

    // Chroma section colors from config
    const chromaColors = chromaFolder 
      ? getColorsInFolder(chromaFolder.id)
      : [].map(applyOverrides);

    // Hue section colors from config
    const hueColors = hueFolder 
      ? getColorsInFolder(hueFolder.id)
      : [].map(applyOverrides);

    // Alpha section colors from config
    const alphaColors = alphaFolder 
      ? getColorsInFolder(alphaFolder.id)
      : [].map(applyOverrides);

    // Helper to create a contrast color definition
    const createContrastColor = (
      id: string,
      name: string,
      folderPath: string,
      backgroundColorId: string,
      foregroundColorId: string,
      contrastLevel: number,
      useAlpha: boolean
    ) => ({
      id,
      mode: 'contrast' as const,
      name: name,
      colorName: name,
      folderPath,
      folderId: folderPath.toLowerCase().replace(/\//g, '-'),
      folderName: folderPath.split('/').pop(),
      backgroundColorId,
      foregroundColorId,
      contrastLevel,
      useAlpha,
      section: 'waitlist'
    });

    // Apply user overrides for contrast colors
    const applyContrastOverrides = (color: any) => {
      const ovr = contrastOverrides[color.id];
      if (!ovr) return color;
      return {
        ...color,
        backgroundColorId: ovr.backgroundColorId ?? color.backgroundColorId,
        foregroundColorId: ovr.foregroundColorId ?? color.foregroundColorId,
        contrastLevel: ovr.contrastLevel ?? color.contrastLevel,
        useAlpha: ovr.useAlpha ?? color.useAlpha,
      };
    };

    // Contrast-based solid section colors from config
    const contrastSolidColors = contrastSolidFolder
      ? getColorsInFolder(contrastSolidFolder.id)
          .filter(c => c.mode === 'contrast')
          .map(applyContrastOverrides)
      : [].map(applyContrastOverrides);

    // Contrast-based transparent section colors from config
    const contrastTransparentColors = contrastTransparentFolder
      ? getColorsInFolder(contrastTransparentFolder.id)
          .filter(c => c.mode === 'contrast')
          .map(applyContrastOverrides)
      : [].map(applyContrastOverrides);

    // Combine all colors in the specified order for dropdown display
    // Use config order, but ensure base colors come first
    const allColors = [
      // Base colors
      ...baseColors,
      // Static colors
      ...staticColorObjects,
      // Linked colors from config (preserve order from config)
      ...lightnessColors,
      ...chromaColors,
      ...hueColors,
      ...alphaColors,
      // Contrast-based colors
      ...contrastSolidColors,
      ...contrastTransparentColors
    ];

    // Calculate each linked color and build precomputed map
    const precomputedColors = new Map();
    
    const calculateLinkedColors = (colors: any[]) => {
      return colors.map(color => {
        const result = calculateLinkedColor(
          allColors,
          color.referenceColorId,
          color.lExpression,
          color.cExpression,
          color.hExpression,
          color.aExpression
        );
        
        // Store in precomputed map
        precomputedColors.set(color.id, result);
        
        return {
          ...color,
          result,
          colorValue: (result as any)?.hex || '#808080',
          oklchValue: (result as any)?.oklch ? formatOklch((result as any).oklch) : 'oklch(0.5 0.1 0)'
        };
      });
    };

    // Add base colors to precomputed map
    precomputedColors.set('base-primary', primaryParsed);
    
    // Calculate secondary color using base color config
    const secondaryColor = baseColors.find(c => c.id === 'base-secondary');
    const secondaryResult = calculateLinkedColor(
      allColors,
      secondaryColor?.referenceColorId || 'base-primary',
      secondaryColor?.lExpression || '',
      secondaryColor?.cExpression || '',
      secondaryColor?.hExpression || '',
      secondaryColor?.aExpression || ''
    );
    precomputedColors.set('base-secondary', secondaryResult);
    
    // Calculate contrast color using base color config
    const contrastColor = baseColors.find(c => c.id === 'base-secondary-on-primary');
    const contrastResult = calculateContrastColor(
      allColors,
      contrastColor?.backgroundColorId || 'base-primary',
      contrastColor?.foregroundColorId || 'base-secondary',
      contrastColor?.contrastLevel || 45,
      contrastColor?.useAlpha || false
    );
    precomputedColors.set('base-secondary-on-primary', contrastResult);
    
    // Add static colors to precomputed map
    staticColorObjects.forEach((color, index) => {
      const parsed = parseColor(color.colorValue);
      precomputedColors.set(color.id, parsed);
    });

    // Calculate contrast colors
    const calculateContrastColors = (colors: any[]) => {
      return colors.map(color => {
        const result = calculateContrastColor(
          allColors,
          color.backgroundColorId,
          color.foregroundColorId,
          color.contrastLevel,
          color.useAlpha
        );
        
        // Store in precomputed map
        precomputedColors.set(color.id, result);
        
        return {
          ...color,
          result,
          colorValue: (result as any)?.hex || '#808080',
          oklchValue: (result as any)?.oklch ? formatOklch((result as any).oklch) : 'oklch(0.5 0.1 0)'
        };
      });
    };

    const contrastSolidResult = calculateContrastColors(contrastSolidColors);
    const contrastTransparentResult = calculateContrastColors(contrastTransparentColors);

    return {
      baseColors,
      lightness: calculateLinkedColors(lightnessColors),
      chroma: calculateLinkedColors(chromaColors),
      hue: calculateLinkedColors(hueColors),
      alpha: calculateLinkedColors(alphaColors),
      contrastSolid: contrastSolidResult,
      contrastTransparent: contrastTransparentResult,
      allColors,
      precomputedColors
    };
  }, [primaryColorValue, linkedOverrides, contrastOverrides, baseColorOverrides, staticColors, configData, transformConfigColor]); // Recalculate when Primary, overrides, base colors, or config changes

  // Update colors when base colors change (only in Tinted mode)
  React.useEffect(() => {
    const root = document.documentElement;
    const isTinted = root.classList.contains('tinted');
    
    // Clean mode: Use static CSS colors, no JavaScript updates needed
    if (!isTinted) {
      // Still update background colors for gradient display
      const primaryColor = linkedColorSections.precomputedColors.get('base-primary');
      const secondaryColor = linkedColorSections.precomputedColors.get('base-secondary');
      
      if (primaryColor && secondaryColor) {
        const primaryHex = (primaryColor as any).hex || '#FF8C00';
        const secondaryHex = (secondaryColor as any).hex || '#FFCD00';
        setBackgroundColors({ primary: primaryHex, secondary: secondaryHex });
      }
      return;
    }
    
    // Tinted mode: Update base colors dynamically (CSS handles all derivations)
    const primaryColor = linkedColorSections.precomputedColors.get('base-primary');
    const secondaryColor = linkedColorSections.precomputedColors.get('base-secondary');
    const secondaryOnPrimaryColor = linkedColorSections.precomputedColors.get('base-secondary-on-primary');
    
    if (primaryColor && secondaryColor && secondaryOnPrimaryColor) {
      const primaryHex = (primaryColor as any).hex || '#FF8C00';
      const secondaryHex = (secondaryColor as any).hex || '#FFCD00';
      
      setBackgroundColors({ primary: primaryHex, secondary: secondaryHex });
      
      // Only update 3 base colors - CSS automatically handles all derivations via oklch(from ...)
      root.style.setProperty('--color-primary', formatOklch((primaryColor as any).oklch));
      root.style.setProperty('--color-secondary', formatOklch((secondaryColor as any).oklch));
      root.style.setProperty('--color-secondary-on-primary', formatOklch((secondaryOnPrimaryColor as any).oklch));
      
      // Preserve explicit user-entered name from initial selection
      setPrimaryColorName(prev => prev || initialDemoRef.current.name);
    }
  }, [linkedColorSections.precomputedColors]);

  // Dynamic favicon based on primary and secondary colors
  React.useEffect(() => {
    const primaryColor = linkedColorSections.precomputedColors.get('base-primary');
    const secondaryOnPrimaryColor = linkedColorSections.precomputedColors.get('base-secondary-on-primary');
    
    if (primaryColor && secondaryOnPrimaryColor) {
      const primaryHex = (primaryColor as any).hex || '#FF8C00';
      const secondaryOnPrimaryHex = (secondaryOnPrimaryColor as any).hex || '#000000';
      
      updateFavicon(primaryHex, secondaryOnPrimaryHex).catch(console.error);
    }
  }, [linkedColorSections.precomputedColors]);

  // Update page metadata
  React.useEffect(() => {
    document.title = 'betterLCH — okLCH colors but better';
    
    // Update or create description meta tag
    let descriptionMeta = document.querySelector("meta[name='description']") as HTMLMetaElement;
    if (!descriptionMeta) {
      descriptionMeta = document.createElement('meta');
      descriptionMeta.name = 'description';
      document.head.appendChild(descriptionMeta);
    }
    descriptionMeta.content = 'Predictable Chroma. Precise contrast. Built for design systems.';

    // Update Open Graph meta tags dynamically based on current host
    // This ensures preview deployments work correctly
    const currentUrl = window.location.origin;
    const currentPath = window.location.pathname;
    const fullUrl = `${currentUrl}${currentPath}`;
    const ogImageUrl = `${currentUrl}/api/og-image`;

    // Helper function to update or create meta tag
    const updateMetaTag = (property: string, content: string, isProperty = true) => {
      const selector = isProperty 
        ? `meta[property="${property}"]` 
        : `meta[name="${property}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update Open Graph tags
    updateMetaTag('og:url', fullUrl);
    updateMetaTag('og:image', ogImageUrl);
    
    // Update Twitter tags
    updateMetaTag('twitter:url', fullUrl, false);
    updateMetaTag('twitter:image', ogImageUrl, false);
  }, []);

  return (
    <>
      <div className="home-background-shader">
        {(() => {
          const primaryColor = linkedColorSections.precomputedColors.get('base-primary');
          const secondaryColor = linkedColorSections.precomputedColors.get('base-secondary');
          const secondaryOnPrimaryColor = linkedColorSections.precomputedColors.get('base-secondary-on-primary');

          const primaryHex = (primaryColor as any)?.hex || backgroundColors.primary;
          const secondaryHex = (secondaryColor as any)?.hex || backgroundColors.secondary;
          const secondaryOnPrimaryHex =
            (secondaryOnPrimaryColor as any)?.hex || secondaryHex;

          const shaderPaused = prefersReducedMotion || !isPageVisible;
          const effectiveSpeed = shaderPaused ? 0 : 1;

          return (
            <GrainGradient
              speed={effectiveSpeed}
              scale={1.66}
              rotation={0}
              offsetX={0}
              offsetY={0}
              softness={0.49}
              intensity={0.5}
              noise={0.15}
              shape="corners"
              frame={26449.438999976162}
              colors={[primaryHex, secondaryHex, secondaryOnPrimaryHex]}
              colorBack="#00000000"
              width="100%"
              height="100%"
              maxPixelCount={2_000_000}
              style={{
                backgroundColor: 'var(--color-background)',
              }}
            />
          );
        })()}
      </div>
      {/* Always-visible banner on small screens */}
      <div
        ref={bannerRef}
        className="small-screen-banner"
        style={{ backgroundColor: bannerColors.bg, color: bannerColors.fg }}
      >
        <p>1280+ wide screen is recommended for a full interactive preview and the best experience</p>
      </div>
      <main 
        className={`demo-page demo-grid-main ${!isInteractive ? 'demo--readonly' : ''}`}
        style={{
          '--gradient-primary': backgroundColors.primary,
          '--gradient-secondary': backgroundColors.secondary
        } as React.CSSProperties}
      >
        <SEOProtection noIndex={false} />
        
        {/* Fixed header */}
        <header className={`demo-fixed-header ${showFixedHeader ? 'demo-fixed-header--visible' : ''}`}>
          <div className="demo-fixed-header__content">
            <h1 className="demo-fixed-header__title" onClick={scrollToTop} style={{ cursor: 'pointer' }}>
              {getEffectiveTheme() === 'dark' ? (
                <LogoDark className="demo-fixed-header__logo" aria-hidden />
              ) : (
                <LogoLight className="demo-fixed-header__logo" aria-hidden />
              )}
              <span style={{ fontSize: '10px', lineHeight: '1', fontWeight: '400', color: 'var(--color-foreground-0)', paddingTop: '9px' }}>1.3</span>
            </h1>
            <Button 
              type="solid" 
              size="medium" 
              className="demo-fixed-header__button" 
              onClick={() => window.open('/app', '_blank', 'noopener,noreferrer')}
            >
              Open the app
            </Button>
          </div>
        </header>
        
        {/* Floating theme selector */}
        <ThemeSelector
          value={themePreference}
          onChange={handleThemePreferenceChange}
          className="floating-theme-selector"
        />
        
        <section className="demo-section demo-section--hero" id="hero">
          <div className=" demo-section_description demo-section_description--hero margin-trim">
            <div className="demo-hero__logo-wrap" aria-hidden>
              {getEffectiveTheme() === 'dark' ? (
                <IconDark className="demo-hero__logo" />
              ) : (
                <IconLight className="demo-hero__logo" />
              )}
            </div>
            <h1 className="title-1">
              <span className="text-gradient">betterLCH</span><br />okLCH colors but better
            </h1>
            <p className="body-text">Not another palette generator or contrast checker. A tool for advanced color manipulations and precise contrast control.</p> {/* Predictable Chroma. Precise contrast. Built for design systems. */}
            <Button 
              type="solid" 
              size="medium" 
              className="button button--large hero-button" 
              onClick={() => window.open('/app', '_blank', 'noopener,noreferrer')}
            >
              Open the app
            </Button>
          </div>
          <div className="demo-section_preview demo-section_preview--hero">
            <FolderHeader
              variant="demo"
              folderName="Base colors"
              isExpanded={true}
              isEditing={false}
              parentPathNames={[]}
              onToggleExpanded={undefined}
              onNameChange={undefined}
              onAddColor={undefined}
              onAddFolder={undefined}
              onStartEditing={undefined}
              onStopEditing={undefined}
              onExpandCollapse={undefined}
              onRename={undefined}
              onMove={undefined}
              onMoveUp={undefined}
              onMoveDown={undefined}
              onDuplicate={undefined}
              onDelete={undefined}
            />
            
            {(linkedColorSections.baseColors || []).map((color) => {
              const precomputed = linkedColorSections.precomputedColors.get(color.id);
              // For static colors, use the original colorValue (maintains OKLCH format with relative chroma)
              // For linked/contrast colors, use the computed hex
              const colorValue = color.mode === 'static' 
                ? color.colorValue 
                : ((precomputed as any)?.hex || color.colorValue || '#808080');
              const oklchValue = (precomputed as any)?.oklch ? formatOklch((precomputed as any).oklch) : 'oklch(0.5 0.1 0)';
              
              return (
                <CardColor
                  key={color.id}
                  variant="demo"
                  mode={color.mode}
                  colorName={color.name}
                  colorValue={colorValue}
                  oklchValue={oklchValue}
                  p3Support={true}
                  isEditing={false}
                  // Static color props
                  onColorValueChange={color.mode === 'static' ? (newVal: string) => {
                    handleBaseColorChange(color.id, 'colorValue', newVal);
                  } : undefined}
                  // Linked color props
                  referenceColorId={color.referenceColorId}
                  lExpression={color.lExpression}
                  cExpression={color.cExpression}
                  hExpression={color.hExpression}
                  aExpression={color.aExpression}
                  onReferenceColorChange={color.mode === 'linked' ? (newId: string) => {
                    handleBaseColorChange(color.id, 'referenceColorId', newId);
                  } : undefined}
                  onLCHAExpressionChange={color.mode === 'linked' ? (component: string, value: string) => {
                    handleBaseColorChange(color.id, `${component}Expression`, value);
                  } : undefined}
                  // Contrast color props
                  backgroundColorId={color.backgroundColorId}
                  foregroundColorId={color.foregroundColorId}
                  contrastLevel={color.contrastLevel}
                  useAlpha={color.useAlpha}
                  onBackgroundColorChange={color.mode === 'contrast' ? (newId: string) => {
                    handleBaseColorChange(color.id, 'backgroundColorId', newId);
                  } : undefined}
                  onForegroundColorChange={color.mode === 'contrast' ? (newId: string) => {
                    handleBaseColorChange(color.id, 'foregroundColorId', newId);
                  } : undefined}
                  onContrastLevelChange={color.mode === 'contrast' ? (newLc: number) => {
                    handleBaseColorChange(color.id, 'contrastLevel', newLc);
                  } : undefined}
                  onContrastAlphaChange={color.mode === 'contrast' ? (useAlpha: boolean) => {
                    handleBaseColorChange(color.id, 'useAlpha', useAlpha);
                  } : undefined}
                  // Common handlers
                  onColorNameChange={() => {}}
                  onModeChange={() => {}}
                  onEditingComplete={() => {}}
                  onMoreAction={() => {}}
                  folderOptions={[]}
                  availableColors={linkedColorSections.allColors as any}
                  precomputedColors={linkedColorSections.precomputedColors as any}
                  currentColorId={color.id}
                />
              );
            })}
          </div>
          <div className="demo-grid-item-features">
              <div className="feature-card card--strong margin-trim">
                <div className="feature-card__content margin-trim">
                  {/* <h3 className="title-3 text-muted" style={{ marginBottom: 0 }}>1</h3> */}
                  <h3 className="title-3 text-strong" style={{ marginTop: 0 }}>Relative Chroma</h3>
                  <p className="body-text text-subtle">Enter a desired amount of Chroma as a percentage</p>
                </div>
                <p className="body-text text-bold text-strong">oklch(0.6 <span className="text-primary-muted">75%</span> 240)</p>
              </div>
              <div className="feature-card card--strong margin-trim">
                <div className="feature-card__content margin-trim">
                  {/* <h3 className="title-3 text-muted" style={{ marginBottom: 0 }}>2</h3> */}
                  <h3 className="title-3 text-strong" style={{ marginTop: 0 }}>LCHA math</h3>
                  <p className="body-text text-subtle">Get new colors by adjusting LCHA values</p>
                </div>
                <p className="body-text text-bold text-strong">
                  L<span className="text-primary-muted"> + </span>50%, H<span className="text-primary-muted"> + </span>30
                </p>
              </div>
              <div className="feature-card card--strong margin-trim">
                <div className="feature-card__content margin-trim">
                  {/* <h3 className="title-3 text-muted" style={{ marginBottom: 0 }}>3</h3> */}
                  <h3 className="title-3 text-strong" style={{ marginTop: 0 }}>APCA/WCAG Contrast</h3>
                  <p className="body-text text-subtle">Not just check — generate colors that meet specific contrast levels</p>
                </div>  
                <p className="body-text text-bold text-strong">Lc: <span className="text-primary-muted">45</span> (AA headlines)/<br/>CR: <span className="text-primary-muted">4.5:1</span> (AA text)</p>
              </div>
              <div className="feature-card card--strong margin-trim">
                <div className="feature-card__content margin-trim">
                  {/* <h3 className="title-3 text-muted" style={{ marginBottom: 0 }}>4</h3> */}
                  <h3 className="title-3 text-strong" style={{ marginTop: 0 }}>Export to Figma</h3>
                  <p className="body-text text-subtle">Apply your theme to Figma files</p>
                </div>
                <p className="body-text text-bold text-strong"><span className="text-primary-muted">Figma variables</span></p>
              </div>
          </div>
        </section>
        
        <section className="demo-section" id="staticColors">
          <div className="demo-section_intro margin-trim" id="staticColorsIntro">
            <h2 className="title-2">Static colors</h2>
            <p className="subtitle">Whole power of OKLCH, and more</p>
            <p className="body-text">No more going out of gamut. No more guessing the right number. <span className="text-bold text-primary-subtle">Relative Chroma</span> allows you to enter the desired amount of Chroma as a <span className="text-bold text-strong">percentage</span> of the possible maximum.</p>
          </div>
          <div className="demo-section_preview">
          <FolderHeader
              variant="demo"
              folderName="Static colors"
              isExpanded={true}
              isEditing={false}
              parentPathNames={[]}
              onToggleExpanded={undefined}
              onNameChange={undefined}
              onAddColor={undefined}
              onAddFolder={undefined}
              onStartEditing={undefined}
              onStopEditing={undefined}
              onExpandCollapse={undefined}
              onRename={undefined}
              onMove={undefined}
              onMoveUp={undefined}
              onMoveDown={undefined}
              onDelete={undefined}
              onDuplicate={undefined}
            />
          {staticColors.map((color, index) => {
            const parsed = color.result;
            const oklchValue = (parsed as any)?.oklch ? formatOklch((parsed as any).oklch) : color.value;

            return (
              <CardColor
                key={index}
                variant="demo"
                mode="static"
                colorName={color.name}
                colorValue={color.value}
                oklchValue={oklchValue}
                p3Support={true}
                isEditing={false}
                onColorNameChange={() => {}}
                onModeChange={() => {}}
                onColorValueChange={!isInteractive ? () => {} : (newVal: string) => {
                  // Update the override for this color
                  setStaticColorOverrides(prev => ({
                    ...prev,
                    [(color as any).index]: newVal
                  }));
                }}
                onEditingComplete={() => {}}
                onReferenceColorChange={() => {}}
                onLCHAExpressionChange={() => {}}
                onBackgroundColorChange={() => {}}
                onForegroundColorChange={() => {}}
                onContrastLevelChange={() => {}}
                onContrastAlphaChange={() => {}}
                onMoreAction={() => {}}
                folderOptions={[]}
                availableColors={[]}
                precomputedColors={new Map() as any}
                currentColorId={`static-${index}`}
              />
            );
          })}
          </div>
        </section>
        
        <section className="demo-section" id="linkedColors">
          <div className="demo-section_intro margin-trim" id="linkedColorsIntro">
            <h2 className="title-2">Linked colors</h2>
            <p className="subtitle">Modify the Reference Color using math expressions</p>
            <h3 className="title-3">Lightness</h3>
            <p className="body-text">Create <span className="text-bold text-primary-subtle">lighter</span> or <span className="text-bold text-primary-subtle">darker</span> variations of the Reference color.</p>
          </div>
          <div className="demo-section_preview" id="lightnessPreview">
            <FolderHeader
              variant="demo"
              folderName="Lightness"
              parentPathNames={['Linked colors'] as any}
              isExpanded={true}
              isEditing={false}
              onToggleExpanded={undefined}
              onNameChange={undefined}
              onAddColor={undefined}
              onAddFolder={undefined}
              onStartEditing={undefined}
              onStopEditing={undefined}
              onExpandCollapse={undefined}
              onRename={undefined}
              onMove={undefined}
              onMoveUp={undefined}
              onMoveDown={undefined}
              onDelete={undefined}
              onDuplicate={undefined}
            />
            {linkedColorSections.lightness.map((color, index) => (
              <CardColor
                key={index}
                variant="demo"
                mode="linked"
                colorName={color.colorName}
                colorValue={color.colorValue}
                oklchValue={color.oklchValue}
                p3Support={true}
                isEditing={false}
                referenceColorId={color.referenceColorId}
                lExpression={color.lExpression}
                cExpression={color.cExpression}
                hExpression={color.hExpression}
                aExpression={color.aExpression}
                onColorNameChange={() => {}}
                onModeChange={() => {}}
                onColorValueChange={() => {}}
                onEditingComplete={() => {}}
                onReferenceColorChange={(newId: string) => {
                  setLinkedOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), referenceColorId: newId }
                  }));
                }}
                onLCHAExpressionChange={(component: string, value: string) => {
                  setLinkedOverrides(prev => {
                    const keyMap: any = { l: 'lExpression', c: 'cExpression', h: 'hExpression', a: 'aExpression' };
                    const k = keyMap[component] || component;
                    return { ...prev, [color.id]: { ...(prev[color.id]||{}), [k]: value } };
                  });
                }}
                onBackgroundColorChange={() => {}}
                onForegroundColorChange={() => {}}
                onContrastLevelChange={() => {}}
                onContrastAlphaChange={() => {}}
                onMoreAction={() => {}}
                folderOptions={[]}
                availableColors={linkedColorSections.allColors as any}
                precomputedColors={linkedColorSections.precomputedColors as any}
                currentColorId={color.id}
              />
            ))}
          </div>
          <div className="demo-section_description margin-trim">
            <h3 className="title-3">Chroma</h3>
            <p className="body-text">Increase or decrease the color's <span className="text-bold text-primary-subtle">intensity</span>.</p>
            <p className="body-text"><span className="text-bold text-strong">Relative Chroma</span> scales consistently across all hues and lightnesses.</p>
          </div>
          <div className="demo-section_preview" id="chromaPreview">
            <FolderHeader
              variant="demo"
              folderName="Chroma"
              parentPathNames={['Linked colors'] as any}
              isExpanded={true}
              isEditing={false}
              onToggleExpanded={undefined}
              onNameChange={undefined}
              onAddColor={undefined}
              onAddFolder={undefined}
              onStartEditing={undefined}
              onStopEditing={undefined}
              onExpandCollapse={undefined}
              onRename={undefined}
              onMove={undefined}
              onMoveUp={undefined}
              onMoveDown={undefined}
              onDelete={undefined}
              onDuplicate={undefined}
            />
            {linkedColorSections.chroma.map((color, index) => (
              <CardColor
                key={index}
                variant="demo"
                mode="linked"
                colorName={color.colorName}
                colorValue={color.colorValue}
                oklchValue={color.oklchValue}
                p3Support={true}
                isEditing={false}
                referenceColorId={color.referenceColorId}
                lExpression={color.lExpression}
                cExpression={color.cExpression}
                hExpression={color.hExpression}
                aExpression={color.aExpression}
                onColorNameChange={() => {}}
                onModeChange={() => {}}
                onColorValueChange={() => {}}
                onEditingComplete={() => {}}
                onReferenceColorChange={(newId: string) => {
                  setLinkedOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), referenceColorId: newId }
                  }));
                }}
                onLCHAExpressionChange={(component: string, value: string) => {
                  setLinkedOverrides(prev => {
                    const keyMap: any = { l: 'lExpression', c: 'cExpression', h: 'hExpression', a: 'aExpression' };
                    const k = keyMap[component] || component;
                    return { ...prev, [color.id]: { ...(prev[color.id]||{}), [k]: value } };
                  });
                }}
                onBackgroundColorChange={() => {}}
                onForegroundColorChange={() => {}}
                onContrastLevelChange={() => {}}
                onContrastAlphaChange={() => {}}
                onMoreAction={() => {}}
                folderOptions={[]}
                availableColors={linkedColorSections.allColors as any}
                precomputedColors={linkedColorSections.precomputedColors as any}
                currentColorId={color.id}
              />
            ))}
          </div>
          <div className="demo-section_description margin-trim">
            <h3 className="title-3">Hue</h3>
            <p className="body-text body-text__margin">Shift Hue values to create <span className="text-bold text-primary-subtle">color harmonies</span>.</p>
          </div>
          <div className="demo-section_preview" id="huePreview">
            <FolderHeader
              variant="demo"
              folderName="Hue"
              parentPathNames={['Linked colors'] as any}
              isExpanded={true}
              isEditing={false}
              onToggleExpanded={undefined}
              onNameChange={undefined}
              onAddColor={undefined}
              onAddFolder={undefined}
              onStartEditing={undefined}
              onStopEditing={undefined}
              onExpandCollapse={undefined}
              onRename={undefined}
              onMove={undefined}
              onMoveUp={undefined}
              onMoveDown={undefined}
              onDelete={undefined}
              onDuplicate={undefined}
            />
            {linkedColorSections.hue.map((color, index) => (
              <CardColor
                key={index}
                variant="demo"
                mode="linked"
                colorName={color.colorName}
                colorValue={color.colorValue}
                oklchValue={color.oklchValue}
                p3Support={true}
                isEditing={false}
                referenceColorId={color.referenceColorId}
                lExpression={color.lExpression}
                cExpression={color.cExpression}
                hExpression={color.hExpression}
                aExpression={color.aExpression}
                onColorNameChange={() => {}}
                onModeChange={() => {}}
                onColorValueChange={() => {}}
                onEditingComplete={() => {}}
                onReferenceColorChange={(newId: string) => {
                  setLinkedOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), referenceColorId: newId }
                  }));
                }}
                onLCHAExpressionChange={(component: string, value: string) => {
                  setLinkedOverrides(prev => {
                    const keyMap: any = { l: 'lExpression', c: 'cExpression', h: 'hExpression', a: 'aExpression' };
                    const k = keyMap[component] || component;
                    return { ...prev, [color.id]: { ...(prev[color.id]||{}), [k]: value } };
                  });
                }}
                onBackgroundColorChange={() => {}}
                onForegroundColorChange={() => {}}
                onContrastLevelChange={() => {}}
                onContrastAlphaChange={() => {}}
                onMoreAction={() => {}}
                folderOptions={[]}
                availableColors={linkedColorSections.allColors as any}
                precomputedColors={linkedColorSections.precomputedColors as any}
                currentColorId={color.id}
              />
            ))}
          </div>
          <div className="demo-section_description margin-trim">
            <h3 className="title-3">Alpha</h3>
            <p className="body-text">Control <span className="text-bold text-primary-subtle">transparency</span> with alpha values and create semi-transparent overlays.</p>
          </div>
          <div className="demo-section_preview" id="alphaPreview">
            <FolderHeader
              variant="demo"
              folderName="Alpha"
              parentPathNames={['Linked colors'] as any}
              isExpanded={true}
              isEditing={false}
              onToggleExpanded={undefined}
              onNameChange={undefined}
              onAddColor={undefined}
              onAddFolder={undefined}
              onStartEditing={undefined}
              onStopEditing={undefined}
              onExpandCollapse={undefined}
              onRename={undefined}
              onMove={undefined}
              onMoveUp={undefined}
              onMoveDown={undefined}
              onDelete={undefined}
              onDuplicate={undefined}
            />
            {linkedColorSections.alpha.map((color, index) => (
              <CardColor
                key={index}
                variant="demo"
                mode="linked"
                colorName={color.colorName}
                colorValue={color.colorValue}
                oklchValue={color.oklchValue}
                p3Support={true}
                isEditing={false}
                referenceColorId={color.referenceColorId}
                lExpression={color.lExpression}
                cExpression={color.cExpression}
                hExpression={color.hExpression}
                aExpression={color.aExpression}
                onColorNameChange={() => {}}
                onModeChange={() => {}}
                onColorValueChange={() => {}}
                onEditingComplete={() => {}}
                onReferenceColorChange={(newId: string) => {
                  setLinkedOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), referenceColorId: newId }
                  }));
                }}
                onLCHAExpressionChange={(component: string, value: string) => {
                  setLinkedOverrides(prev => {
                    const keyMap: any = { l: 'lExpression', c: 'cExpression', h: 'hExpression', a: 'aExpression' };
                    const k = keyMap[component] || component;
                    return { ...prev, [color.id]: { ...(prev[color.id]||{}), [k]: value } };
                  });
                }}
                onBackgroundColorChange={() => {}}
                onForegroundColorChange={() => {}}
                onContrastLevelChange={() => {}}
                onContrastAlphaChange={() => {}}
                onMoreAction={() => {}}
                folderOptions={[]}
                availableColors={linkedColorSections.allColors as any}
                precomputedColors={linkedColorSections.precomputedColors as any}
                currentColorId={color.id}
              />
            ))}
          </div>
        </section>
        
        <section className="demo-section" id="contrastColors">
          <div className="demo-section_intro margin-trim" id="contrastColorsIntro">
            <h2 className="title-2">Contrast-based colors</h2>
            <p className="subtitle">Generate colors that meet specific APCA or WCAG contrast requirements</p>
            <h3 className="title-3">Opaque</h3>
            <p className="body-text">By default, a fully opaque color is generated — just set the target <span className="text-bold text-primary-subtle">APCA Lightness contrast</span> or <span className="text-bold text-primary-subtle">WCAG Contrast ratio</span> (see WCAG in the app).</p>
          </div>
          <div className="demo-section_preview" id="solidPreview">
            <FolderHeader
              variant="demo"
              folderName="Solid"
              parentPathNames={['Contrast-based'] as any}
              isExpanded={true}
              isEditing={false}
              onToggleExpanded={undefined}
              onNameChange={undefined}
              onAddColor={undefined}
              onAddFolder={undefined}
              onStartEditing={undefined}
              onStopEditing={undefined}
              onExpandCollapse={undefined}
              onRename={undefined}
              onMove={undefined}
              onMoveUp={undefined}
              onMoveDown={undefined}
              onDelete={undefined}
              onDuplicate={undefined}
            />
            {linkedColorSections.contrastSolid?.map((color, index) => (
              <CardColor
                key={index}
                variant="demo"
                mode="contrast"
                colorName={color.colorName}
                colorValue={color.colorValue}
                oklchValue={color.oklchValue}
                p3Support={true}
                isEditing={false}
                backgroundColorId={color.backgroundColorId}
                foregroundColorId={color.foregroundColorId}
                contrastLevel={color.contrastLevel}
                useAlpha={color.useAlpha}
                onColorNameChange={() => {}}
                onModeChange={() => {}}
                onColorValueChange={() => {}}
                onEditingComplete={() => {}}
                onReferenceColorChange={() => {}}
                onLCHAExpressionChange={() => {}}
                onBackgroundColorChange={(newId: string) => {
                  setContrastOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), backgroundColorId: newId }
                  }));
                }}
                onForegroundColorChange={(newId: string) => {
                  setContrastOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), foregroundColorId: newId }
                  }));
                }}
                onContrastLevelChange={(newLc: number) => {
                  setContrastOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), contrastLevel: newLc }
                  }));
                }}
                onContrastAlphaChange={(useAlpha: boolean) => {
                  setContrastOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), useAlpha }
                  }));
                }}
                onMoreAction={() => {}}
                folderOptions={[]}
                availableColors={linkedColorSections.allColors as any}
                precomputedColors={linkedColorSections.precomputedColors as any}
                currentColorId={color.id}
              />
            ))}
          </div>
          <div className="demo-section_description margin-trim">
            <h3 className="title-3">Transparent</h3>
            <p className="body-text">With <span className="text-bold text-primary-subtle">Alpha</span> checked, you'll get a transparent color while still meeting the target contrast. Ideal for overlays and layered interfaces.</p>
          </div>
          <div className="demo-section_preview" id="transparentPreview">
            <FolderHeader
              variant="demo"
              folderName="Transparent"
              parentPathNames={['Contrast-based'] as any}
              isExpanded={true}
              isEditing={false}
              onToggleExpanded={undefined}
              onNameChange={undefined}
              onAddColor={undefined}
              onAddFolder={undefined}
              onStartEditing={undefined}
              onStopEditing={undefined}
              onExpandCollapse={undefined}
              onRename={undefined}
              onMove={undefined}
              onMoveUp={undefined}
              onMoveDown={undefined}
              onDelete={undefined}
              onDuplicate={undefined}
            />
            {linkedColorSections.contrastTransparent?.map((color, index) => (
              <CardColor
                key={index}
                variant="demo"
                mode="contrast"
                colorName={color.colorName}
                colorValue={color.colorValue}
                oklchValue={color.oklchValue}
                p3Support={true}
                isEditing={false}
                backgroundColorId={color.backgroundColorId}
                foregroundColorId={color.foregroundColorId}
                contrastLevel={color.contrastLevel}
                useAlpha={color.useAlpha}
                onColorNameChange={() => {}}
                onModeChange={() => {}}
                onColorValueChange={() => {}}
                onEditingComplete={() => {}}
                onReferenceColorChange={() => {}}
                onLCHAExpressionChange={() => {}}
                onBackgroundColorChange={(newId: string) => {
                  setContrastOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), backgroundColorId: newId }
                  }));
                }}
                onForegroundColorChange={(newId: string) => {
                  setContrastOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), foregroundColorId: newId }
                  }));
                }}
                onContrastLevelChange={(newLc: number) => {
                  setContrastOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), contrastLevel: newLc }
                  }));
                }}
                onContrastAlphaChange={(useAlpha: boolean) => {
                  setContrastOverrides(prev => ({
                    ...prev,
                    [color.id]: { ...(prev[color.id]||{}), useAlpha }
                  }));
                }}
                onMoreAction={() => {}}
                folderOptions={[]}
                availableColors={linkedColorSections.allColors as any}
                precomputedColors={linkedColorSections.precomputedColors as any}
                currentColorId={color.id}
              />
            ))}
          </div>
        </section>

        <footer className="footer" ref={footerRef}>
          <div className="footer__form-card card--strong">
            <div className="footer__form-content margin-trim">
              <h2 className="title-3 text-strong">Start building your theme</h2>
              <p className="body-text text-subtle">
                Open the <span className="text-bold">betterLCH</span> app and see it in action.
              </p>
              <Button
                type="solid"
                size="medium"
                className="button"
                onClick={() => window.open('/app', '_blank', 'noopener,noreferrer')}
              >
                Open the app
              </Button>
            </div>
          </div>
            <div className="footer__credits">
              <p className="body-text text-subtle">
                Made by <span className="text-bold text-strong">Solo Cube</span>:
              </p>
              <p className="body-text text-subtle"><a href="https://x.com/solo_cube" target="_blank" rel="noopener noreferrer">X/Twitter</a></p>
              <p className="body-text text-subtle"><a href="https://medium.com/@solo_cube" target="_blank" rel="noopener noreferrer">Medium</a></p>
            </div>
        </footer>
      </main>
    </>
  );
}


